import React from 'react';

import clsx from 'clsx';
import { Box, ButtonBase, makeStyles, Theme, createStyles } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { FiMoreHorizontal } from 'react-icons/fi';

export interface ChatReaction {
  emoji: string;
  count: number;
  // The user is one of those who reacted with it.
  mine?: boolean;
}

interface Props {
  reactions: ChatReaction[];
  // Adds or takes back the user's reaction.
  onToggle?: (emoji: string) => void;
  // Who reacted with what.
  onDetails?: () => void;
}

// Reactions under a bubble: tap one to join it or take yours back.
export const ChatReactions: React.FC<Props> = ({ reactions, onToggle, onDetails }) => {
  const classes = useStyles();

  if (!reactions.length) {
    return null;
  }

  return (
    <Box className={classes.root}>
      {reactions.map(reaction => (
        <ButtonBase
          key={reaction.emoji}
          className={clsx(classes.chip, { [classes.mine]: reaction.mine })}
          onClick={() => onToggle?.(reaction.emoji)}
          aria-pressed={!!reaction.mine}
          aria-label={`${reaction.emoji} ${reaction.count}`}
        >
          <span className={classes.emoji}>{reaction.emoji}</span>
          {reaction.count > 1 && <span>{reaction.count}</span>}
        </ButtonBase>
      ))}
      {onDetails && (
        <ButtonBase className={classes.chip} onClick={onDetails} aria-label="See who reacted">
          <FiMoreHorizontal size={14} />
        </ButtonBase>
      )}
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: { display: 'flex', flexWrap: 'wrap', gap: 4 },
    chip: {
      height: 26,
      padding: '0 8px',
      gap: 4,
      borderRadius: 13,
      fontSize: 13,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
    },
    mine: {
      color: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
    },
    emoji: { fontSize: 15, lineHeight: 1 },
  })
);
